import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link2, Plus, X } from "lucide-react";
import { LinkedRecordPicker } from "./LinkedRecordPicker";
import { cn } from "@/lib/utils";

interface LinkedRecordCellProps {
  fieldId: string;
  sourceRecordId: string;
  targetTableId: string;
  linkedRecordIds: string[];
  onLinksChange: (recordIds: string[]) => void;
  isEditing?: boolean;
  isSelected?: boolean;
  allowMultiple?: boolean;
  readOnly?: boolean;
}

export const LinkedRecordCell = ({
  fieldId,
  sourceRecordId,
  targetTableId,
  linkedRecordIds,
  onLinksChange,
  isEditing = false,
  isSelected = false,
  allowMultiple = true,
  readOnly = false,
}: LinkedRecordCellProps) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [linkedRecords, setLinkedRecords] = useState<any[]>([]);
  const [primaryField, setPrimaryField] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (linkedRecordIds.length > 0 && targetTableId) {
      fetchLinkedRecords();
    } else {
      setLinkedRecords([]);
    }
  }, [linkedRecordIds, targetTableId]);

  const fetchLinkedRecords = async () => {
    if (!targetTableId || linkedRecordIds.length === 0) return;
    
    setLoading(true);
    try {
      // Get primary field for display
      const { data: fields } = await supabase
        .from("fields")
        .select("*")
        .eq("table_id", targetTableId)
        .order("order_index")
        .limit(1);

      if (fields && fields.length > 0) {
        setPrimaryField(fields[0]);
      }

      // Get linked records
      const { data: records, error } = await supabase
        .from("records")
        .select("*")
        .in("id", linkedRecordIds);

      if (error) throw error;
      setLinkedRecords(records || []);
    } catch (error) {
      console.error("Failed to fetch linked records:", error);
    } finally {
      setLoading(false);
    }
  };

  const getRecordDisplayValue = (record: any) => {
    if (!primaryField || !record.data) {
      return `Record ${record.id.slice(0, 8)}`;
    }
    return record.data[primaryField.name] || `Record ${record.id.slice(0, 8)}`;
  };

  const handleRemoveLink = (recordId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (readOnly) return;
    
    const newLinks = linkedRecordIds.filter((id) => id !== recordId);
    onLinksChange(newLinks);
  };

  const handleOpenPicker = () => {
    if (readOnly) return;
    setPickerOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-1 p-1">
        <div className="h-5 w-16 bg-muted animate-pulse rounded" />
      </div>
    );
  }

  const displayRecords = linkedRecords.slice(0, 3);
  const remainingCount = linkedRecords.length - 3;

  return (
    <>
      <div
        className={cn(
          "flex flex-wrap items-center gap-1 min-h-[36px] p-1",
          isSelected && !isEditing && "ring-2 ring-primary ring-inset",
          !readOnly && "cursor-pointer hover:bg-muted/50"
        )}
        onClick={handleOpenPicker}
      >
        {linkedRecords.length === 0 ? (
          <span className="text-muted-foreground text-sm flex items-center gap-1">
            {!readOnly && <Plus className="h-3 w-3" />}
            {readOnly ? "—" : "Link record"}
          </span>
        ) : (
          <>
            {displayRecords.map((record) => (
              <Badge
                key={record.id}
                variant="secondary"
                className="gap-1 pr-1 bg-primary/10 text-primary hover:bg-primary/20"
              >
                <Link2 className="h-3 w-3" />
                <span className="max-w-[100px] truncate">
                  {getRecordDisplayValue(record)}
                </span>
                {!readOnly && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 ml-1 hover:bg-destructive/20 hover:text-destructive"
                    onClick={(e) => handleRemoveLink(record.id, e)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </Badge>
            ))}
            {remainingCount > 0 && (
              <Badge variant="outline" className="text-xs">
                +{remainingCount} more
              </Badge>
            )}
          </>
        )}
      </div>

      <LinkedRecordPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        targetTableId={targetTableId}
        fieldId={fieldId}
        sourceRecordId={sourceRecordId}
        selectedRecordIds={linkedRecordIds}
        onSelect={onLinksChange}
        allowMultiple={allowMultiple}
      />
    </>
  );
};
